import MathText from "@/components/MathText";
import PaperBookmarkButton from "@/components/PaperBookmarkButton";
import { BookOpen01Icon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { FormattedDate, FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { Link } from "react-router";
import { PaperCardFragment$key } from "./__generated__/PaperCardFragment.graphql";

const MAX_AUTHORS = 3;

const PaperCardFragment = graphql`
  fragment PaperCardFragment on Paper {
    arxivId
    openalexId
    title
    abstract
    authors
    primaryCategory
    venue
    citedByCount
    announcedOn
    ...PaperBookmarkButtonFragment
  }
`;

export interface PaperCardProps {
  paper: PaperCardFragment$key;
}

export default function PaperCard(props: PaperCardProps) {
  const paper = useFragment(PaperCardFragment, props.paper);
  const shownAuthors = paper.authors.slice(0, MAX_AUTHORS);
  const moreAuthors = paper.authors.length - shownAuthors.length;

  return (
    <div className="group relative">
      <Link
        to={`/papers/${paper.arxivId ?? paper.openalexId}`}
        className="w-full h-full p-5 rounded-xl border border-grey-200 flex flex-col gap-3 bg-white transition-shadow shadow-sm group-hover:shadow-lg"
      >
        <div className="flex flex-row justify-start items-start gap-3">
          <div className="bg-brand-50 flex items-center justify-center rounded-lg w-8 h-8 shrink-0">
            <BookOpen01Icon className="text-brand-500 w-5 h-5" />
          </div>
          <p className="flex-1 pr-12 text-grey-900 text-lg font-semibold leading-7 transition-colors group-hover:text-brand-500 line-clamp-2">
            <MathText>{paper.title}</MathText>
          </p>
        </div>
        <p className="text-grey-600 text-sm line-clamp-1">
          {shownAuthors.join(", ")}
          {moreAuthors > 0 && (
            <FormattedMessage
              defaultMessage=" +{count} more"
              values={{ count: moreAuthors }}
            />
          )}
        </p>
        <p className="text-grey-600 text-sm line-clamp-3">
          <MathText>{paper.abstract}</MathText>
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-auto text-grey-500 text-sm">
          {paper.primaryCategory && (
            <Badge size="sm">{paper.primaryCategory}</Badge>
          )}
          {paper.primaryCategory && paper.primaryCategory !== "quant-ph" && (
            <Badge size="sm" variant="secondary">
              <FormattedMessage defaultMessage="Cross-listed" />
            </Badge>
          )}
          {paper.arxivId && <span>arXiv:{paper.arxivId}</span>}
          {paper.arxivId && paper.venue && <span aria-hidden>•</span>}
          {paper.venue && <span className="line-clamp-1">{paper.venue}</span>}
          <span aria-hidden>•</span>
          {/* A calendar date: formatted in UTC so it doesn't shift a day west of UTC. */}
          <FormattedDate
            dateStyle="medium"
            value={paper.announcedOn}
            timeZone="UTC"
          />
          {paper.citedByCount != null && (
            <>
              <span aria-hidden>•</span>
              <span>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# citation} other {# citations}}"
                  values={{ count: paper.citedByCount }}
                />
              </span>
            </>
          )}
        </div>
      </Link>
      {/* Outside the link: buttons can't be nested in anchors. */}
      <div className="absolute top-4 right-4">
        <PaperBookmarkButton paper={paper} />
      </div>
    </div>
  );
}
